/**
 * integrationOwnership — the single place that decides "is this ours?"
 *
 * Before this existed the question was answered inline, in two spellings that
 * did not agree:
 *
 *   record.ownership?.isIntegrationOwned ?? record.source === 'bookinglayer'   // 6 files
 *   record.ownership?.source === 'bookinglayer'                                // 9 files
 *
 * The second silently answers "no" whenever the backend omits `ownership`,
 * which the mappers leave `undefined` — so the same record could read as
 * integration-owned on one screen and local on another.
 *
 * Both spellings also hardcode the vendor. The predicate below asks the
 * structural question instead: is the source anything other than ours?
 *
 * Vendor identity is still available via `integrationSourceOf()` for the
 * surfaces that legitimately brand one — an icon, a label, a deep link.
 */

import { LOCAL_SOURCE, type IntegrationOwnedRecord, type IntegrationSource } from '@/shared/types/integration';

/** `true` for any source that is not ours. Empty/absent is not an integration. */
export function isIntegrationSource(source: string | null | undefined): boolean {
  return typeof source === 'string' && source !== '' && source !== LOCAL_SOURCE;
}

/**
 * Is this record mirrored from an external system?
 *
 * Trusts the backend's `ownership.isIntegrationOwned` when present; otherwise
 * falls back to the source, checking the `ownership` object first and then the
 * flat field, because different mapper paths populate different ones.
 *
 * An unrecognised source counts as **owned by an integration**. That is the
 * safe direction: the worst case is a record shown read-only that could have
 * been edited, versus edits made to a mirrored record and lost on next sync.
 */
export function isIntegrationOwned(record: IntegrationOwnedRecord | null | undefined): boolean {
  if (!record) return false;

  const flagged = record.ownership?.isIntegrationOwned;
  if (typeof flagged === 'boolean') return flagged;

  return isIntegrationSource(record.ownership?.source ?? record.source);
}

/** Created in Shaka, and therefore ours to edit. The exact inverse. */
export function isLocallyOwned(record: IntegrationOwnedRecord | null | undefined): boolean {
  return !isIntegrationOwned(record);
}

/**
 * Which system this came from, for the surfaces that name it.
 *
 * Returns the source verbatim — it never collapses an unknown vendor into
 * `'manual'`. Use it to brand ("Bookinglayer"), never to decide editability;
 * `isIntegrationOwned()` is the predicate for that.
 */
export function integrationSourceOf(
  record: IntegrationOwnedRecord | null | undefined,
): IntegrationSource {
  const source = record?.ownership?.source ?? record?.source;
  return isIntegrationSource(source) ? (source as IntegrationSource) : LOCAL_SOURCE;
}
